import he from 'he';
import { fluidRange } from 'polished';
import { createElement } from 'react';
import styled, { css } from 'styled-components';

import { xs, xl } from './breakpoints';
import { baseFontSize } from './variables';

/**
 * convert pixel to rem
 * @param {string} fontSize
 */
export const pixelToRem = (fontSize) => `${fontSize.slice(0, -2) / baseFontSize.slice(0, -2)}rem`;

/**
 *
 * @param {string} lineHeight
 * @param {string} fontSize
 * @returns `line-height` as ratio
 */
export function lineHeightAsRatio(lineHeight, fontSize) {
  // remove units, return ratio
  return `${lineHeight.slice(0, -2) / fontSize.slice(0, -2)}`;
}

export const srOnlyMixin = css`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

/**
 * Screen reader only span
 */
export const SROnly = styled.span`
  ${srOnlyMixin}
`;

export function fluidProp(props, fromSize, toSize) {
  // Accepts multiple props as an array
  const normalizedProps = typeof props === 'string' ? [props] : props;

  return fluidRange(
    normalizedProps.map((prop) => ({
      prop,
      fromSize,
      toSize,
    })),
    `${xs}px`,
    `${xl}px`
  );
}

export function focusVisible(rules) {
  return css`
    .js-focus-visible &:focus {
      outline: none;
    }

    .js-focus-visible &:focus.focus-visible {
      ${rules}
    }
  `;
}

/**
 * whenProp
 *
 * Applies the provided style when the provided prop is true
 *
 * @param {String|String[]} props - name of boolean component prop
 * @param {ThemedCssFunction} style - css`` block with style to apply when true
 */
export const whenProp = (props, style) => {
  const normalizedProps = typeof props === 'string' ? [props] : props;
  return (componentProps) => normalizedProps.some((prop) => componentProps[prop]) && style;
};

/**
 * unlessProp
 *
 * Applies the provided style when the provided prop is false
 *
 * @param {String|String[]} props - name of boolean component prop
 * @param {ThemedCssFunction} style - css`` block with style to apply when false
 */
export const unlessProp = (props, style) => {
  const normalizedProps = typeof props === 'string' ? [props] : props;
  return (componentProps) => normalizedProps.every((prop) => !componentProps[prop]) && style;
};

/**
 * whenPropOrSelector
 *
 * Applies the provided style when the provided prop is true or on provided css states
 *
 * @param {String|String[]} props - name of boolean component prop
 * @param {String|String[]} states - Array or string with css states such as hover, focus, active
 * @param {ThemedCssFunction} style - css`` block with style to apply when false
 */
export const whenPropOrSelector = (props, selector, style) => css`
  ${selector} {
    ${style}
  }
  ${whenProp(props, style)}
`;

// global canonical helper - extracted from blog.
export const getCanonicalURL = (url) => {
  if (!url || typeof url !== 'string') {
    return null;
  }

  return url
    .replace(/\/$/, '')
    .replace(/^http:\/\//i, 'https://')
    .split('?')[0];
};

export function toLocaleDateString(date, locale = null) {
  return new Date(date).toLocaleDateString(locale || 'en-us', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function renderHTMLDangerously(rawHTML) {
  const newElement = createElement('div', {
    dangerouslySetInnerHTML: { __html: he.decode(rawHTML) },
  });
  return newElement;
}

export const getCorrectAnchorLink = (link) => {
  if (link.charAt(0) === '#') {
    return link;
  }

  return `#${link}`;
};
